import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { windowLabel } from "@/lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DOW = ["S","M","T","W","T","F","S"];

export default function CalendarPage() {
  const [orders, setOrders] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/orders/my").then((r) => setOrders(r.data));
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const k = o.pickup_date || (o.created_at || "").slice(0, 10);
      if (!k) return;
      (map[k] = map[k] || []).push(o);
    });
    return map;
  }, [orders]);

  const days = useMemo(() => buildMonth(cursor.y, cursor.m), [cursor]);
  const dayKey = (d) => `${d.y}-${String(d.m + 1).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="calendar-title">
        <CalIcon strokeWidth={2.5}/> Pickup Calendar
      </h1>
      <p className="text-slate-600 mb-4">Tap any date to see details.</p>

      <div className="card-clay p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCursor(prev(cursor))} className="btn-push-outline p-2" data-testid="cal-prev"><ChevronLeft size={16}/></button>
          <div className="font-fredoka font-bold text-lg">{MONTHS[cursor.m]} {cursor.y}</div>
          <button onClick={() => setCursor(next(cursor))} className="btn-push-outline p-2" data-testid="cal-next"><ChevronRight size={16}/></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
          {DOW.map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {days.map((d, i) => {
            const k = dayKey(d);
            const o = byDate[k];
            const isToday = k === todayKey;
            return (
              <button
                key={i}
                onClick={() => setSelected(k)}
                className={`aspect-square rounded-xl border-2 text-sm flex flex-col items-center justify-center transition-all relative ${
                  d.outside ? "border-transparent text-slate-300" :
                  isToday ? "border-green-500 bg-green-50 text-green-800 font-bold" :
                  selected === k ? "border-amber-500 bg-amber-50" :
                  "border-slate-100 hover:border-slate-300 bg-white"
                }`}
                data-testid={`cal-day-${d.d}`}
              >
                <span>{d.d}</span>
                {o && o.length > 0 && (
                  <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-green-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="card-clay p-5 mt-4" data-testid="day-details">
          <div className="flex items-center justify-between">
            <div className="font-fredoka font-bold">{selected}</div>
            <button onClick={() => setSelected(null)} className="text-xs font-bold text-slate-500">Close</button>
          </div>
          {(byDate[selected] || []).length === 0 ? (
            <div className="text-sm text-slate-400 mt-3">No pickups on this day.</div>
          ) : (
            <ul className="mt-3 space-y-2">
              {(byDate[selected] || []).map((o) => (
                <li key={o.id} className="rounded-xl bg-slate-50 p-3 text-sm flex items-center justify-between" data-testid={`cal-order-${o.id}`}>
                  <div>
                    <div className="font-semibold">{windowLabel(o.window)}</div>
                    <div className="text-xs text-slate-500">{o.bags} bag(s) · {o.community} · ${o.total_amount.toFixed(2)}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${{
                    scheduled: "bg-blue-100 text-blue-800",
                    picked_up: "bg-green-100 text-green-800",
                    completed: "bg-green-100 text-green-800",
                    skipped: "bg-amber-100 text-amber-800",
                    canceled: "bg-slate-200 text-slate-700",
                  }[o.status] || "bg-slate-100"}`}>{o.status}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/schedule" className="btn-push-green inline-block mt-4 px-4 py-2 text-sm">Schedule another pickup</Link>
        </div>
      )}
    </div>
  );
}

function buildMonth(y, m) {
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const startDow = first.getDay();
  const days = [];
  // previous month padding
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(y, m, -i);
    days.push({ d: d.getDate(), m: d.getMonth(), y: d.getFullYear(), outside: true });
  }
  for (let i = 1; i <= last.getDate(); i++) {
    days.push({ d: i, m, y, outside: false });
  }
  while (days.length % 7 !== 0) {
    const d = new Date(y, m + 1, days.length - last.getDate() - startDow + 1);
    days.push({ d: d.getDate(), m: d.getMonth(), y: d.getFullYear(), outside: true });
  }
  return days;
}
function prev({ y, m }) { return m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }; }
function next({ y, m }) { return m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }; }
